import * as React from "react";
import { Link, Redirect, useParams } from "wouter";
import { Header } from "src/components/Header";
import { Page } from "src/components/Page";
import { getGoalRecord } from "src/database/queries/goals";
import { getHabitsForGoal, moveHabitWithinGoal } from "src/database/queries/habits";
import { getTasksForGoal, moveTaskWithinGoal } from "src/database/queries/tasks";
import { createEmptyTask } from "src/database/factories";
import { taskFormContext } from "src/modules/daily/TaskForm/taskFormContext";
import { TaskTreeEditor, type TaskTreeEditorOnMoveItem } from "src/modules/daily/TaskTreeEditor";
import { AnyTaggedItem } from "src/types";
import { Tree } from "src/utils/Tree";
import { useLiveQuery } from "dexie-react-hooks";
import { PlusIcon } from "lucide-react";
import { Ordering } from "src/database/models/Ordering";
import styled from "styled-components";

export function GoalPage() {
  const { id } = useParams<{ id: string }>();
  const taskForm = React.useContext(taskFormContext);
  const goal = useLiveQuery(() => (id ? getGoalRecord(id) : Promise.resolve(null)), [id]);
  const goalHabits = useLiveQuery(() => (id ? getHabitsForGoal(id) : Promise.resolve([])), [id]);
  const goalTasks = useLiveQuery(() => (id ? getTasksForGoal(id) : Promise.resolve([])), [id]);
  const goalItems = React.useMemo<AnyTaggedItem[]>(
    () => [...(goalHabits ?? []), ...(goalTasks ?? [])],
    [goalHabits, goalTasks],
  );
  const goalItemTree = React.useMemo(() => Tree.build(goalItems), [goalItems]);

  console.log("goalTree", goalItemTree, goalItems);

  const onMoveItemInGoal = React.useCallback<TaskTreeEditorOnMoveItem>(
    (item, parent, index) => {
      if (!id) return;
      if (item.type === "habit") {
        return moveHabitWithinGoal(id, item, parent, index);
      }
      return moveTaskWithinGoal(id, item, parent, index);
    },
    [id],
  );

  if (!id) {
    return <Redirect to="/goals" replace />;
  }

  if (goal === undefined) {
    return (
      <Page maxWidth={600}>
        <Header />
      </Page>
    );
  }

  if (goal === null) {
    return (
      <Page maxWidth={600}>
        <Header title="Goal not found" />
        <BackRow>
          <Link href="/goals">Back to Goals</Link>
        </BackRow>
      </Page>
    );
  }

  return (
    <Page maxWidth={600}>
      <Header title={goal.name}>
        <Header.Button
          icon={PlusIcon}
          onClick={() => {
            const rootGoalTasks = (goalTasks ?? []).filter((t) => t.parentId === null);
            taskForm.create(
              createEmptyTask({
                goalId: goal.id,
                components: ["goals"],
                order: Ordering.insertAt(rootGoalTasks, (t) => t.order, 0),
              }),
            );
          }}
        >
          New Task
        </Header.Button>
      </Header>
      <GoalSection data-testid="goal-page-habits">
        <GoalSectionHeading>Items</GoalSectionHeading>
        <TaskTreeEditor items={goalItemTree} onMoveItem={onMoveItemInGoal} />
      </GoalSection>
    </Page>
  );
}

const BackRow = styled.p`
  margin: 0 10px 20px;
  a {
    color: var(--theme-color-primary);
  }
`;

const GoalSection = styled.section`
  margin: 0 10px 20px;
`;

const GoalSectionHeading = styled.h2`
  font-size: 14px;
  font-weight: 300;
  margin: 16px 0 10px;
`;
